// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// Licensed under the MIT License — see LICENSE file in the repo root.
// SPDX-License-Identifier: MIT

/**
 * SEC-R2-11 — cho service worker biết phiên nào đang đăng nhập.
 *
 * `public/sw.js` xếp hàng POST nhật ký/sự kiện lúc mất mạng vào IndexedDB
 * `tracepro-offline-v2` rồi tự phát lại. SW không đọc được cookie httpOnly nên
 * không biết ai đang đăng nhập — trước đây nó phát lại MỌI dòng bằng cookie hiện
 * tại, tức là U2 gửi dòng của U1. Trang ghi `{tenantId,userId}` của phiên vào kho
 * `meta` (khoá `activeOwner`); SW đóng dấu dòng mới bằng giá trị đó và chỉ phát lại
 * dòng trùng. Đăng xuất → ghi null → SW giữ mọi dòng.
 *
 * ⚠️ Lược đồ ở đây PHẢI khớp `initIDB()` trong public/sw.js (ai mở trước thì người
 * đó nâng cấp). Đổi một bên là đổi cả hai — test sw-queue-owner.test.ts kiểm.
 */

import { toOwner } from "@tracepro/mobile-host";
import type { SessionOwner } from "@tracepro/mobile-host";

export const SW_IDB_NAME = "tracepro-offline-v2";
export const SW_IDB_VERSION = 3;
export const SW_STORES = ["pending_journal", "pending_events"] as const;
export const SW_META_STORE = "meta";
export const SW_ACTIVE_OWNER_KEY = "activeOwner";

export function openSwQueueDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const r = indexedDB.open(SW_IDB_NAME, SW_IDB_VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      for (const name of SW_STORES) {
        if (!db.objectStoreNames.contains(name)) {
          const s = db.createObjectStore(name, { keyPath: "id", autoIncrement: true });
          s.createIndex("queuedAt", "queuedAt");
        }
      }
      if (!db.objectStoreNames.contains(SW_META_STORE)) db.createObjectStore(SW_META_STORE);
    };
    r.onsuccess = () => {
      const db = r.result;
      // Nhường nâng cấp lược đồ cho bên khác (SW mới) thay vì chặn nó.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    r.onerror = () => reject(r.error);
    // Bị SW cũ giữ kết nối (đang chuyển giao bản deploy): đừng treo chuỗi ghi.
    r.onblocked = () => reject(new Error("indexedDB upgrade blocked"));
  });
}

// Ghi nối đuôi nhau: lúc tải trang phiên đi null → U1 rất nhanh; hai lần ghi chạy
// song song có thể để lần xoá về đích sau lần ghi.
let writeChain: Promise<void> = Promise.resolve();

/** Ghi (hoặc xoá khi null) phiên đang đăng nhập cho SW. Không bao giờ ném. */
export function writeSwActiveOwner(owner: SessionOwner | null): Promise<void> {
  writeChain = writeChain.then(() => doWrite(owner));
  return writeChain;
}

async function doWrite(owner: SessionOwner | null): Promise<void> {
  try {
    const db = await openSwQueueDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(SW_META_STORE, "readwrite");
      const st = tx.objectStore(SW_META_STORE);
      const o = toOwner(owner);
      if (o) st.put(o, SW_ACTIVE_OWNER_KEY);
      else st.delete(SW_ACTIVE_OWNER_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
    db.close();
  } catch {
    /* không có IndexedDB → SW cũng không có hàng đợi để lọc */
  }
}

export async function readSwActiveOwner(): Promise<SessionOwner | null> {
  try {
    const db = await openSwQueueDb();
    const v = await new Promise<unknown>((resolve) => {
      const g = db.transaction(SW_META_STORE, "readonly").objectStore(SW_META_STORE).get(SW_ACTIVE_OWNER_KEY);
      g.onsuccess = () => resolve(g.result);
      g.onerror = () => resolve(null);
    });
    db.close();
    return toOwner(v as SessionOwner | null);
  } catch {
    return null;
  }
}
